import { MarketingFeatureGrid } from '@/components/marketing/blocks/marketing-feature-grid'
import { MarketingSplitNarrative } from '@/components/marketing/blocks/marketing-split-narrative'
import { MarketingWaitlistBand } from '@/components/marketing/blocks/marketing-waitlist-band'
import { MarketingSection } from '@/components/marketing/marketing-section'
import { WaitlistFormShell } from '@/components/marketing/waitlist-form-shell'
import { LumieBrandMark } from '@/components/landing/lumie/lumie-brand-mark'
import { LumieMarketingFooter } from '@/components/landing/lumie/lumie-marketing-footer'
import { LumieOrbHero } from '@/components/landing/lumie/lumie-orb-hero'
import { LumieProductShowcase } from '@/components/landing/lumie/lumie-product-showcase'
import { MarketingPageShell } from '@/components/landing/marketing-page-shell'
import { useGuestSurfaceTheme } from '@/hooks/use-guest-surface-theme'
import { resolveLanding, type ClientWithLanding } from '@/landing/resolve-landing'
import { CURRENT_CLIENT } from '@/constants'

const FEATURES = [
  {
    icon: '◎',
    title: 'Connect your data',
    description:
      'Bring together records, wearables, medications, symptoms, sleep, cycle, mood and lifestyle information.',
  },
  {
    icon: '⌁',
    title: 'See patterns',
    description: 'Track changes over time and understand what may be connected across your health story.',
  },
  {
    icon: '✦',
    title: 'AI health summaries',
    description: 'Create clear, structured summaries to prepare for appointments and reduce repetition.',
  },
  {
    icon: '⌘',
    title: 'Share with consent',
    description: 'Keep control of what you share, who sees it and when access is granted.',
  },
] as const

/**
 * Lumie early-access marketing landing — founder mock layout with static waitlist shell.
 */
export function LumieEarlyAccessLanding() {
  const { guestTheme, guestPreference, setGuestTheme } = useGuestSurfaceTheme()
  const client = CURRENT_CLIENT as ClientWithLanding
  const { headerAuth, navLinks } = resolveLanding(client, guestTheme)

  return (
    <MarketingPageShell
      stickyOverlayHeader
      stickyHeaderSolid
      stickyHeaderContentInset
      transparentShell={false}
      signInLabel={headerAuth.signInLabel}
      registerLabel={headerAuth.registerLabel}
      hideRegister={headerAuth.hideRegister}
      hideSignIn={headerAuth.hideSignIn}
      guestTheme={guestTheme}
      guestPreference={guestPreference}
      onGuestThemeChange={setGuestTheme}
      navLinks={navLinks}
      headerBrand={<LumieBrandMark />}
      headerSurfaceVariant="cream"
      footer={<LumieMarketingFooter />}
    >
      <LumieOrbHero />

      <MarketingSection id="problem" variant="cream">
        <MarketingSplitNarrative
          title="Healthcare is fragmented. Your data is too."
          lead="Test results, symptoms, medications, wearable data and medical history often sit in separate places. That makes it harder to see patterns, prepare for appointments and advocate for your care."
          cta={{ label: 'Join the waitlist', to: '#waitlist' }}
        />
      </MarketingSection>

      <MarketingSection id="features">
        <MarketingFeatureGrid
          title="One connected view of your health."
          lead="Lumie helps organise your information so you can understand the full picture, not just isolated moments."
          items={FEATURES}
        />
      </MarketingSection>

      <MarketingSection variant="cream">
        <LumieProductShowcase />
      </MarketingSection>

      <MarketingSection id="privacy">
        <MarketingSplitNarrative
          title="Data where it belongs, with you."
          lead="Lumie is being built around patient ownership, privacy and consent. Your health data should support you, not disappear into disconnected systems."
          cta={{ label: 'Be first to access Lumie', to: '#waitlist' }}
        />
      </MarketingSection>

      <MarketingSection id="waitlist" className="pb-24">
        <MarketingWaitlistBand
          title="Be first to access Lumie."
          lead="Join the early access list and help shape the future of patient-owned healthcare."
        >
          <div className="mt-8">
            <WaitlistFormShell />
          </div>
        </MarketingWaitlistBand>
      </MarketingSection>
    </MarketingPageShell>
  )
}
